import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  ChevronDown, ChevronUp, Search, HelpCircle, MessageSquare,
  GraduationCap, CreditCard, FileText, Globe, Users, Shield
} from 'lucide-react';

const FAQ = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [openItems, setOpenItems] = useState({});

  const categories = [
    {
      name: "Getting Started",
      icon: GraduationCap,
      color: "emerald",
      questions: [
        {
          q: "What is Wayo?",
          a: "Wayo is an AI centric comprehensive college application platform and academic advisor for international students that helps students apply to multiple colleges and universities through a single, streamlined application process. Think of it as your one stop shop for college admissions and student visa guidance."
        },
        {
          q: "Is Wayo free to use?",
          a: "Creating an account and browsing colleges is free. Our AI features (ALEC, WONG, Faith, Ashish, Wasif) require credits, which you can purchase from our pricing page. Guides like the F1 Visa Guide, Why College Matters, and Paying for College are always free."
        },
        {
          q: "How do I create an account?",
          a: "Click 'Get Started' on our homepage and follow the simple signup process. You'll need an email address, and we'll guide you through setting up your student profile."
        },
        {
          q: "Can international students use Wayo?",
          a: "Absolutely! Who else will? Wayo is built for international students."
        }
      ]
    },
    {
      name: "Applications",
      icon: FileText,
      color: "blue",
      questions: [
        {
          q: "How many colleges can I apply to?",
          a: "You can apply to as many colleges as you'd like. We recommend having a balanced list of reach, match, and safety schools, typically 8 to 12 colleges total."
        },
        {
          q: "Can I save my application and finish later?",
          a: "Yes! Your application is automatically saved as you work. You can log out and return anytime to continue where you left off."
        },
        {
          q: "How do I request letters of recommendation?",
          a: "Through your dashboard, you can invite recommenders by email. They'll receive instructions on how to submit their letters directly through Wayo."
        },
        {
          q: "What's the difference between Early Decision and Regular Decision?",
          a: "Early Decision (ED) is a binding commitment. If accepted, you must attend. Early Action (EA) is non binding. Regular Decision has later deadlines and gives you until May 1 to decide."
        }
      ]
    },
    {
      name: "Payments & Fees",
      icon: CreditCard,
      color: "purple",
      questions: [
        {
          q: "How do application fees work?",
          a: "Application fees are set by individual colleges and typically range from $0 to $90. You'll pay these fees when submitting each application. We accept all major credit cards."
        },
        {
          q: "Can I get a fee waiver?",
          a: "Yes! If you qualify for free/reduced lunch, are a foster youth, or meet other eligibility criteria, you can apply for fee waivers through your dashboard."
        },
        {
          q: "Is my payment information secure?",
          a: "Absolutely. We use Stripe, a PCI compliant payment processor, to handle all transactions. Your payment details are never stored on our servers."
        }
      ]
    },
    {
      name: "International Students",
      icon: Globe,
      color: "teal",
      questions: [
        {
          q: "What documents do I need as an international student?",
          a: "You'll typically need: transcripts (translated if necessary), test scores (SAT/ACT, TOEFL/IELTS), passport copy, financial documentation, and any credential evaluations required."
        },
        {
          q: "How do I submit English proficiency scores?",
          a: "You can self-report your TOEFL/IELTS scores initially. Most colleges require official scores sent directly from the testing agency before enrollment."
        },
        {
          q: "Do you help with visa applications?",
          a: "Yes! Check out our comprehensive F1 Visa Guide which walks you through the entire student visa application process, including DS160 preparation and interview tips."
        }
      ]
    },
    {
      name: "Counselors & Parents",
      icon: Users,
      color: "orange",
      questions: [
        {
          q: "How can my counselor access my application?",
          a: "Your counselor needs to create a Counselor Portal account. You can then link them to your profile, giving them access to submit transcripts and recommendations."
        },
        {
          q: "Can my parents see my application?",
          a: "Yes! Through our Parent Portal, you can invite parents to view your application progress. They can also manage payments and review financial aid information."
        },
        {
          q: "What can counselors do on Wayo?",
          a: "Counselors can: view student applications, submit official transcripts, write and submit recommendations, track deadlines, and communicate with students."
        }
      ]
    },
    {
      name: "Privacy & Security",
      icon: Shield,
      color: "red",
      questions: [
        {
          q: "How is my data protected?",
          a: "We use industry standard encryption (AES256) for all data storage and transmission. Your information is only shared with colleges you apply to."
        },
        {
          q: "Who can see my application?",
          a: "Only you, people you authorize (counselors, parents), and the admissions offices of colleges you apply to can see your application."
        },
        {
          q: "Can I delete my account?",
          a: "Yes. You can request account deletion from your settings. Note that some information may be retained per legal requirements or for submitted applications."
        }
      ]
    }
  ];

  const toggleItem = (categoryIndex, questionIndex) => {
    const key = `${categoryIndex}-${questionIndex}`;
    setOpenItems(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const filteredCategories = categories.map(category => ({
    ...category,
    questions: category.questions.filter(
      q => q.q.toLowerCase().includes(searchQuery.toLowerCase()) ||
           q.a.toLowerCase().includes(searchQuery.toLowerCase())
    )
  })).filter(category => category.questions.length > 0);

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white py-16">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <HelpCircle className="w-8 h-8" />
          </div>
          <h1 className="text-4xl font-bold mb-4">Frequently Asked Questions</h1>
          <p className="text-lg opacity-90 mb-8">
            Find answers to common questions about Wayo and the college application process.
          </p>
          <div className="relative max-w-xl mx-auto">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search for answers..."
              className="pl-12 py-6 text-lg bg-white text-gray-900 border-0"
            />
          </div>
        </div>
      </div>

      {/* FAQ Content */}
      <div className="max-w-4xl mx-auto px-4 py-12">
        {filteredCategories.map((category, catIndex) => (
          <div key={catIndex} className="mb-10">
            <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
              <category.icon className={`w-5 h-5 text-${category.color}-600`} />
              {category.name}
            </h2>
            <div className="space-y-3">
              {category.questions.map((item, qIndex) => {
                const isOpen = openItems[`${catIndex}-${qIndex}`];
                return (
                  <Card key={qIndex} className="overflow-hidden">
                    <button
                      onClick={() => toggleItem(catIndex, qIndex)}
                      className="w-full p-4 text-left flex items-center justify-between hover:bg-gray-50 transition"
                    >
                      <span className="font-medium pr-4">{item.q}</span>
                      {isOpen ? (
                        <ChevronUp className="w-5 h-5 text-gray-400 flex-shrink-0" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-gray-400 flex-shrink-0" />
                      )}
                    </button>
                    {isOpen && (
                      <CardContent className="pt-0 pb-4 px-4">
                        <p className="text-gray-600">{item.a}</p>
                      </CardContent>
                    )}
                  </Card>
                );
              })}
            </div>
          </div>
        ))}

        {filteredCategories.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-500">No results found for "{searchQuery}"</p>
            <Button variant="link" onClick={() => setSearchQuery('')}>
              Clear search
            </Button>
          </div>
        )}
      </div>

      {/* Still Need Help */}
      <div className="bg-gray-50 py-16">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <MessageSquare className="w-12 h-12 text-emerald-600 mx-auto mb-4" />
          <h2 className="text-2xl font-bold mb-4">Still Have Questions?</h2>
          <p className="text-gray-600 mb-6">
            Can't find what you're looking for? Our support team is here to help.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button className="bg-emerald-600 hover:bg-emerald-700">
                Contact Support
              </Button>
            </Link>
            <Link to="/how-it-works">
              <Button variant="outline">
                How Wayo Works
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FAQ;
